import {
  APP_INITIALIZER,
  ErrorHandler,
  inject,
  NgModule,
  provideAppInitializer
} from '@angular/core'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { en_US, NZ_I18N } from 'ng-zorro-antd/i18n'
import { registerLocaleData } from '@angular/common'
import en from '@angular/common/locales/en'
import { FormsModule } from '@angular/forms'
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi
} from '@angular/common/http'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { BiotModule } from '../shared/modules/biot/biot.module'
import { TokenInterceptor } from '../core/interceprors/token.interceptor'
import { RequestInterceptor } from '../core/interceprors/request.interceptor'
import { NzNotificationService } from 'ng-zorro-antd/notification'
import { RootStoreModule } from '../store/root-store.module'
import { PersistenceService } from '../shared/services/persistence.service'
import { NzLayoutModule } from 'ng-zorro-antd/layout'
import * as AllIcons from '@ant-design/icons-angular/icons'
import { IconDefinition } from '@ant-design/icons-angular'
import { NZ_ICONS, NzIconModule } from 'ng-zorro-antd/icon'
import { IconService } from '../shared/services/icon.service'
import { MomentModule } from 'ngx-moment'
import { NgxEchartsModule } from 'ngx-echarts'
import { AngularFireModule } from '@angular/fire/compat'
import { environment } from '../environments/environment'
import { provideTranslateService } from '@ngx-translate/core'
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader'
import { NzMessageService } from 'ng-zorro-antd/message'
import { ServiceWorkerModule } from '@angular/service-worker'
import { ReportLoaderComponent } from './pages/reports/components/report-loader/report-loader.component'
import { ScreenSaverModalComponent } from './layouts/modals/components/screen-saver-modal/screen-saver-modal.component'
import { CompareTimesInterceptor } from '../core/interceprors/compare-times.interceptor'
import { ClockUpdateModalComponent } from './layouts/modals/components/clock-update-modal/clock-update-modal.component'
import { NzCollapseModule } from 'ng-zorro-antd/collapse'
// import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx'
import { OpenFeature } from '@openfeature/web-sdk'
import { ConfigProvider } from '../core/provider/config-provider'
import { SignDisclaimerComponent } from './layouts/modals/components/sign-disclaimer/sign-disclaimer.component'
import { WarningModalComponent } from './layouts/mobile-ionic-components/component/warning-modal/warning-modal.component'
import { MenuComponent } from './layouts/menu/components/menu/menu.component'
import { GlobalErrorHandlerService } from '../shared/services/global-error-handler.service'
import { HiddenFromPccWindowComponent } from './layouts/modals/components/hidden-from-pcc-window/hidden-from-pcc-window.component'
import { LayoutModule } from '@angular/cdk/layout'
import { NoRoleSelectedComponent } from './layouts/modals/components/no-role-selected/no-role-selected.component'
import { NgxPermissionsModule } from 'ngx-permissions'
import { deviceConnectivityProviders } from '../shared/providers/device-connectivity.provider'
import { hoverPrefetchProviders } from 'ngx-hover-preload'
import { traceInterceptor } from '../core/interceprors/trace.interceptor'
import { LowChargingComponent } from './pages/auth/components/login/low-charging/low-charging.component'
import { HalfScreenModalComponent } from './features/half-screen-modal/half-screen-modal.component'
import { UnsupportedBrowsersComponent } from './features/unsupported-browsers/unsupported-browsers.component'
import { ChargerConnectedComponent } from './features/charger-connected/charger-connected.component'
import { NotifyBeforeRestartComponent } from './features/notify-before-restart/notify-before-restart.component'
import { NotificationComponent } from './layouts/modals/components/notification/notification.component'
import { SystemStatusModalComponent } from './features/system-status-modal/system-status-modal.component'
import { DailyBriefingComponent } from './personal-assistant/daily-briefing/daily-briefing.component'
import { PaOnboardingComponent } from './personal-assistant/onboarding/pa-onboarding.component'
import { BootstrapConfigService } from '../shared/services/bootstrap-config.service'
import { TourHostComponent } from './features/tour-guide/components/tour-host/tour-host.component'
import { provideTourGuides } from './features/tour-guide/tour.tokens'
import { WELCOME_USERS_TOUR } from './features/tour-guide/tours/welcome-users.tour'

registerLocaleData(en)

export function StartUpIconServiceFactory(iconService: IconService) {
  return () => iconService.loadSvgIcon()
}

const antDesignIcons = AllIcons as {
  [key: string]: IconDefinition
}
const icons: IconDefinition[] = Object.keys(antDesignIcons).map(
  key => antDesignIcons[key]
)

export function initializeOpenFeature(
  configProvider: ConfigProvider
): () => Promise<void> {
  return async () => {
    await configProvider.initialize()
    OpenFeature.setProvider(configProvider)
  }
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    RootStoreModule,
    BiotModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    FormsModule,
    NzLayoutModule,
    MomentModule,
    ReportLoaderComponent,
    AngularFireModule.initializeApp(environment.firebase),
    NgxEchartsModule.forRoot({
      echarts: () => import('echarts')
    }),
    ServiceWorkerModule.register('./sw.js', {
      enabled: window.location.hostname !== 'localhost',
      registrationStrategy: 'registerImmediately'
    }),
    ScreenSaverModalComponent,
    ClockUpdateModalComponent,
    NzCollapseModule,
    SignDisclaimerComponent,
    WarningModalComponent,
    MenuComponent,
    HiddenFromPccWindowComponent,
    LayoutModule,
    NoRoleSelectedComponent,
    NgxPermissionsModule.forRoot(),
    LowChargingComponent,
    HalfScreenModalComponent,
    UnsupportedBrowsersComponent,
    ChargerConnectedComponent,
    NotifyBeforeRestartComponent,
    NotificationComponent,
    SystemStatusModalComponent,
    DailyBriefingComponent,
    PaOnboardingComponent,
    TourHostComponent,
    NzIconModule
  ],
  providers: [
    ...provideTourGuides(WELCOME_USERS_TOUR),
    PersistenceService,
    NzNotificationService,
    NzMessageService,
    ...deviceConnectivityProviders,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeOpenFeature,
      deps: [ConfigProvider],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (bootstrapConfigService: BootstrapConfigService) => () =>
        bootstrapConfigService.setLoginUrl(),
      deps: [BootstrapConfigService],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestInterceptor,
      multi: true
    },
    { provide: NZ_I18N, useValue: en_US },
    { provide: NZ_ICONS, useValue: icons },
    provideAppInitializer(() => {
      const initializerFn = StartUpIconServiceFactory(inject(IconService))
      return initializerFn()
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CompareTimesInterceptor,
      multi: true
    },
    // InAppBrowser,
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([traceInterceptor])
    ),
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    hoverPrefetchProviders,
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '../assets/i18n/',
        suffix: '.json'
      }),
      defaultLanguage: 'en'
    })
  ]
})
export class AppModule {}
